import { instance } from '@api/setup';
import { AuthContext } from 'hooks/useAuth';
import React, { FC, useContext, useMemo } from 'react';
import { IChildrenProps } from 'types/common';
import { useLocalStorage } from 'usehooks-ts';

export interface IProjectData {
	currentProject: string;
	setCurrentProject: React.Dispatch<React.SetStateAction<string>>;
	setCurrentProjectStorage: React.Dispatch<React.SetStateAction<string>>;
	currentCompany: string;
	setCurrentCompany: React.Dispatch<React.SetStateAction<string>>;
	projectsList: string[];
}

export const ProjectContext = React.createContext<IProjectData>({
	currentProject: '',
	setCurrentProject: () => {},
	setCurrentProjectStorage: () => {},
	currentCompany: '',
	setCurrentCompany: () => {},
	projectsList: []
});

export const ProjectProvider: FC<IChildrenProps> = ({ children }) => {
	const { consoleUser } = useContext(AuthContext);
	const [currentProjectStorage, setCurrentProjectStorage] = useLocalStorage(
		'currentProject',
		''
	);

	const projectFromLocalStorage = useMemo(
		() =>
			consoleUser.projects?.find((proj) => proj?.id === currentProjectStorage),
		[consoleUser.projects, currentProjectStorage]
	);

	const [currentProject, setCurrentProject] = React.useState<string>(
		projectFromLocalStorage?.id ||
			(consoleUser?.projects && consoleUser?.projects[0]?.id) ||
			''
	);

	const [currentCompany, setCurrentCompany] = React.useState<string>(
		projectFromLocalStorage?.companyId ||
			(consoleUser?.projects && consoleUser?.projects[0].companyId) ||
			''
	);

	const projectsList = useMemo(
		() =>
			(consoleUser?.projects || []).map(
				(project: { name: string }) => project.name
			),
		[consoleUser?.projects]
	);

	const project = useMemo<IProjectData>(
		() => ({
			currentProject,
			setCurrentProject,
			setCurrentProjectStorage,
			currentCompany,
			setCurrentCompany,
			projectsList
		}),
		[
			currentProject,
			setCurrentProject,
			setCurrentProjectStorage,
			currentCompany,
			setCurrentCompany,
			projectsList
		]
	);

	instance.defaults.headers.common['x-Current-Project'] = currentProject;
	instance.defaults.headers.common['x-Current-Company'] = currentCompany;

	return (
		<ProjectContext.Provider value={project}>
			{children}
		</ProjectContext.Provider>
	);
};
